import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Avatar } from '@components/ui';
import { useUsers } from '@hooks';
import { useThemeStore } from '@store';

// Apple HIG: Roles con iconos y colores semánticos
const PROJECT_ROLES = [
  { value: 'OWNER', label: 'Propietario', icon: '👑', color: '#92400E' },
  { value: 'ADMIN', label: 'Administrador', icon: '⚙️', color: '#6D28D9' },
  { value: 'MEMBER', label: 'Miembro', icon: '👤', color: '#065F46' },
  { value: 'VIEWER', label: 'Observador', icon: '👁️', color: '#475569' },
];

// Apple HIG: Iconos consistentes
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function MemberModal({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  existingMembers = [],
  isLoading = false,
}) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('MEMBER');
  const [searchTerm, setSearchTerm] = useState('');
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const { data: usersData, isLoading: usersLoading } = useUsers();

  // Filter out users who are already members
  const availableUsers = useMemo(() => {
    const users = usersData?.data || [];
    const existingUserIds = existingMembers.map((m) => m.userId || m.user?.id);
    return users.filter((user) => !existingUserIds.includes(user.id));
  }, [usersData, existingMembers]);

  // Filter by search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return availableUsers;
    const term = searchTerm.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
    );
  }, [availableUsers, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
      setSelectedRole('MEMBER');
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    await onSubmit({
      projectId,
      userId: parseInt(selectedUserId),
      role: selectedRole,
    });
  };

  const selectedUser = availableUsers.find(
    (u) => u.id === parseInt(selectedUserId)
  );

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';
  };

  // Apple HIG: Estilos con soporte dark mode y jerarquía visual
  const labelStyle = {
    display: 'block',
    fontSize: '13px', // Apple HIG: Label size
    fontWeight: '600',
    color: isDark ? '#94A3B8' : '#64748B',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  };

  // Apple HIG: Input con icono de búsqueda
  const searchContainerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const searchIconStyle = {
    position: 'absolute',
    left: '14px',
    color: isDark ? '#64748B' : '#94A3B8',
    pointerEvents: 'none',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px 12px 42px', // Apple HIG: Padding para icono
    backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
    borderRadius: '12px',
    fontSize: '15px', // Apple HIG: Body text size
    color: isDark ? '#F1F5F9' : '#1E293B',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  // Apple HIG: Lista de usuarios con mejor diseño
  const userListStyle = {
    maxHeight: '240px',
    overflowY: 'auto',
    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
    borderRadius: '12px',
    backgroundColor: isDark ? '#1E293B' : '#FFFFFF',
  };

  const userItemStyle = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px', // Apple HIG: Touch target
    cursor: 'pointer',
    backgroundColor: isSelected
      ? (isDark ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)')
      : 'transparent',
    borderBottom: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`,
    transition: 'background-color 0.15s ease',
  });

  // Apple HIG: Preview del usuario seleccionado
  const selectedPreviewStyle = {
    padding: '16px',
    backgroundColor: isDark ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
    borderRadius: '12px',
    border: `1px solid ${isDark ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`,
  };

  // Apple HIG: Selector de rol visual
  const roleGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  };

  const roleOptionStyle = (isSelected, roleColor) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    borderRadius: '12px',
    border: `2px solid ${isSelected ? roleColor : (isDark ? '#334155' : '#E2E8F0')}`,
    backgroundColor: isSelected
      ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)')
      : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Agregar Miembro"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!selectedUserId}
          >
            Agregar al Equipo
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Apple HIG: Search Input con icono */}
            <div>
              <label style={labelStyle}>Buscar</label>
            <div style={searchContainerStyle}>
              <span style={searchIconStyle}>
                <SearchIcon />
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre o email..."
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#6366F1';
                  e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDark ? '#334155' : '#E2E8F0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Apple HIG: User List mejorada */}
          <div>
            <label style={labelStyle}>Usuarios Disponibles</label>
            <div style={userListStyle}>
              {usersLoading ? (
                <div style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: isDark ? '#64748B' : '#94A3B8'
                }}>
                  <div style={{ marginBottom: '8px' }}>Cargando...</div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: isDark ? '#64748B' : '#94A3B8'
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                    {searchTerm ? '🔍' : '✓'}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {searchTerm
                      ? 'No se encontraron usuarios'
                      : 'Todos los usuarios ya son miembros'}
                  </div>
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const isSelected = selectedUserId === user.id.toString();
                  return (
                    <div
                      key={user.id}
                      onClick={() => setSelectedUserId(user.id.toString())}
                      style={userItemStyle(isSelected)}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = isDark
                            ? 'rgba(255,255,255,0.03)'
                            : 'rgba(0,0,0,0.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Avatar initials={getInitials(user.name)} size="sm" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          color: isDark ? '#F1F5F9' : '#1E293B',
                          marginBottom: '2px'
                        }}>
                          {user.name}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: isDark ? '#64748B' : '#94A3B8'
                        }}>
                          {user.email}
                        </div>
                      </div>
                      {isSelected && (
                        <span style={{ color: '#6366F1' }}>
                          <CheckIcon />
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Apple HIG: Preview del usuario seleccionado */}
          {selectedUser && (
            <div style={selectedPreviewStyle}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#6366F1',
                marginBottom: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Seleccionado
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Avatar initials={getInitials(selectedUser.name)} size="md" />
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isDark ? '#F1F5F9' : '#1E293B'
                  }}>
                    {selectedUser.name}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: isDark ? '#94A3B8' : '#64748B'
                  }}>
                    {selectedUser.email}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Apple HIG: Role Selection visual con cards */}
          <div>
            <label style={labelStyle}>Rol en el Proyecto</label>
            <div style={roleGridStyle}>
              {PROJECT_ROLES.map((role) => {
                const isSelected = selectedRole === role.value;
                return (
                  <div
                    key={role.value}
                    onClick={() => setSelectedRole(role.value)}
                    style={roleOptionStyle(isSelected, role.color)}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = isDark ? '#475569' : '#CBD5E1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = isDark ? '#334155' : '#E2E8F0';
                      }
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{role.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: isSelected ? role.color : (isDark ? '#F1F5F9' : '#1E293B')
                      }}>
                        {role.label}
                      </div>
                    </div>
                    {isSelected && (
                      <span style={{ color: role.color }}>
                        <CheckIcon />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <p style={{
            fontSize: '13px',
            color: isDark ? '#64748B' : '#94A3B8',
            margin: 0,
            lineHeight: '1.5'
          }}>
            El rol determina qué acciones puede realizar el miembro dentro del proyecto.
          </p>
        </div>
      </form>
    </Modal>
  );
}

MemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  projectId: PropTypes.number,
  existingMembers: PropTypes.array,
  isLoading: PropTypes.bool,
};
